import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Tray, ArrowsClockwise, SealCheck } from '@phosphor-icons/react';
import { useBootstrapPorVincular, useBootstrapPorVerificar } from '@/hooks/useBootstrap';
import { GrupoCard } from '@/components/bootstrap/GrupoCard';
import { LoteCard } from '@/components/bootstrap/LoteCard';
import { AutoVincularPanel } from '@/components/bootstrap/AutoVincularPanel';
import { VerificarList } from '@/components/bootstrap/VerificarList';

// Un cubo de la bandeja. Los TRES siempre se muestran: esconder "Sin
// sugerencia" = esos grupos no se procesan = la bandeja no se vacia (9.0).
function Cubo({
  titulo, descripcion, cuantos, children,
}: { titulo: string; descripcion: string; cuantos: number; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-baseline gap-2 border-b border-brand-gray-200 pb-2">
        <h2 className="text-lg font-semibold text-brand-gray-500">{titulo}</h2>
        <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-xs font-bold text-brand-gray-400">
          {cuantos}
        </span>
        <span className="text-xs text-brand-gray-400">{descripcion}</span>
      </div>
      {cuantos === 0 ? <p className="text-sm italic text-brand-gray-400">Nada por aquí.</p> : children}
    </section>
  );
}

// Las tarjetas de lote son mas altas (traen 1-5 codigos adentro): 2 columnas,
// no 3, para que los codigos se lean sin apretarse.
const GRID_LOTES = 'grid gap-3 lg:grid-cols-2';
const GRID_CODIGOS = 'grid gap-3 md:grid-cols-2 xl:grid-cols-3';

// Las DOS vistas son separadas, no un cubo más (9.5): "Por vincular" es
// trabajo activo —decidir a qué lote va cada trámite— y "Verificar" es la
// rampa de QA sobre lo que el auto-vínculo ya resolvió. Mezclarlas haría que
// la bandeja no se vea vacía nunca, que es justo lo que hay que lograr (9.0).
type Vista = 'vincular' | 'verificar';

function Tab({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
        activa
          ? 'bg-brand-gray-500 text-white'
          : 'border border-brand-gray-300 bg-white text-brand-gray-500 hover:bg-brand-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

export function BootstrapPage() {
  const [vista, setVista] = useState<Vista>('vincular');
  const { data, isLoading, isError, error, isFetching, refetch } = useBootstrapPorVincular();
  // La cola de verificación se consulta siempre (no solo en su pestaña): su
  // contador va en la pestaña, que es lo que avisa que hay trabajo ahí.
  const { data: porVerificar } = useBootstrapPorVerificar();

  // DOS numeros distintos y los dos importan: `totalDecisiones` es cuántas
  // tarjetas hay que tocar (un lote con 5 códigos es UNA), y `totalCodigos` es
  // cuántos grupos hay abajo — que es lo que cuenta el auto-vínculo. Mostrar
  // solo el segundo era lo que hacía ver la maratón como 258 decisiones.
  const totalDecisiones =
    (data?.lotes.length ?? 0) + (data?.ambiguos.length ?? 0) + (data?.sinSugerencia.length ?? 0);
  const totalCodigos =
    (data?.lotes.reduce((a, l) => a + l.cantidadCodigos, 0) ?? 0) +
    (data?.ambiguos.length ?? 0) + (data?.sinSugerencia.length ?? 0);
  const totalVerificar = porVerificar?.length ?? 0;

  return (
    <div className="h-full overflow-auto bg-brand-bg">
      <div className="mx-auto max-w-7xl px-6 py-5">
        {/* Cabecera */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Link
            to="/tramites"
            className="inline-flex items-center gap-1 rounded-lg border border-brand-gray-300 bg-white px-3 py-1.5 text-sm text-brand-gray-500 hover:bg-brand-gray-100"
          >
            <ArrowLeft size={16} weight="bold" /> Trámites
          </Link>
          <div className="flex items-center gap-2">
            <Tray size={22} weight="bold" className="text-brand-green-200" />
            <h1 className="text-xl font-semibold text-brand-gray-500">Bandeja de bootstrap CFIA</h1>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-brand-gray-300 bg-white px-3 py-1.5 text-sm text-brand-gray-500 hover:bg-brand-gray-100 disabled:opacity-50"
          >
            <ArrowsClockwise size={16} weight="bold" className={isFetching ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>

        {/* Las dos vistas, con sus contadores: lo que falta decidir y lo que
            falta verificar. Los dos números son el estado de la maratón. */}
        <div className="mb-4 flex flex-wrap gap-2">
          <Tab activa={vista === 'vincular'} onClick={() => setVista('vincular')}>
            <Tray size={16} weight="bold" />
            Por vincular
            <span className={`rounded-full px-1.5 text-xs font-bold ${
              vista === 'vincular' ? 'bg-white/20' : 'bg-brand-gray-100 text-brand-gray-400'
            }`}>
              {isLoading || isError ? '—' : totalDecisiones}
            </span>
          </Tab>
          <Tab activa={vista === 'verificar'} onClick={() => setVista('verificar')}>
            <SealCheck size={16} weight="bold" />
            Verificar auto-vinculados
            <span className={`rounded-full px-1.5 text-xs font-bold ${
              vista === 'verificar' ? 'bg-white/20' : 'bg-brand-gray-100 text-brand-gray-400'
            }`}>
              {totalVerificar}
            </span>
          </Tab>
        </div>

        {vista === 'verificar' ? (
          <VerificarList />
        ) : (
          <>
            <p className="mb-4 max-w-3xl text-sm text-brand-gray-400">
              La unidad de trabajo es el <strong>lote</strong>: un mismo lote acumula varios códigos (su APC y
              su CFIA, más reintentos), y como solo puede tener un trámite vigente, se decide una vez con todos
              sus códigos a la vista. Una vez vinculado, los correos futuros de esos códigos amarran solos.
              {data && totalDecisiones > 0 && (
                <> Hoy: <strong>{totalDecisiones}</strong> decisiones sobre <strong>{totalCodigos}</strong> códigos.</>
              )}
            </p>

            {/* El atajo de B4: antes de vincular a mano, sacarse de encima lo
                que no tiene nada que decidir. */}
            {data && totalDecisiones > 0 && (
              <AutoVincularPanel onIrAVerificar={() => setVista('verificar')} />
            )}

            {isLoading && <p className="text-sm text-brand-gray-400">Cargando bandeja…</p>}
            {isError && (
              <p className="text-sm text-brand-red-200">Error al cargar la bandeja: {(error as Error)?.message}</p>
            )}

            {data && totalDecisiones === 0 && (
              <div className="rounded-2xl border border-brand-gray-200 bg-white p-10 text-center">
                <Tray size={40} weight="bold" className="mx-auto mb-3 text-brand-gray-300" />
                <p className="font-medium text-brand-gray-500">Bandeja vacía</p>
                <p className="text-sm text-brand-gray-400">No hay correos por vincular.</p>
              </div>
            )}

            {data && totalDecisiones > 0 && (
              <>
                <Cubo
                  titulo="Lotes sugeridos"
                  descripcion="un candidato claro — todos los códigos del lote, juntos"
                  cuantos={data.lotes.length}
                >
                  <div className={GRID_LOTES}>
                    {data.lotes.map(l => <LoteCard key={l.idLote} lote={l} />)}
                  </div>
                </Cubo>
                <Cubo
                  titulo="Ambiguos"
                  descripcion="2+ candidatos de lote — el lote todavía no está decidido, va por código"
                  cuantos={data.ambiguos.length}
                >
                  <div className={GRID_CODIGOS}>
                    {data.ambiguos.map(g => <GrupoCard key={g.codigo} grupo={g} />)}
                  </div>
                </Cubo>
                <Cubo
                  titulo="Sin sugerencia"
                  descripcion="sin candidato — se resolverá con el buscador manual"
                  cuantos={data.sinSugerencia.length}
                >
                  <div className={GRID_CODIGOS}>
                    {data.sinSugerencia.map(g => <GrupoCard key={g.codigo} grupo={g} />)}
                  </div>
                </Cubo>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
